import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import LandingMainHeader from './LandingMainHeader'
import {
  ArrowRight,
  Bot,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Coins,
  LayoutDashboard,
  LockKeyhole,
  Mail,
  Phone,
  ShieldCheck,
  Sparkles,
  UserRound,
  Users,
  Workflow,
  X
} from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import {
  getOrCreateAnonymousId,
  getStoredSessionId,
  registerLandingEvent,
  registerLandingVisit,
  setStoredSessionId
} from '../../services/landingTracking'

const TURNSTILE_SITE_KEY = (() => {
  if (import.meta.env.VITE_TURNSTILE_SITE_KEY) return import.meta.env.VITE_TURNSTILE_SITE_KEY
  const hostname = window.location.hostname
  return hostname === 'localhost' || hostname === '127.0.0.1'
    ? '0x4AAAAAACewzlyKjy6eQokF'
    : '0x4AAAAAACewdq3FV9qGlLZJ'
})()

const PRODUCT_CODE = 'AUTOMATIONROI'
const STEP_COUNT = 7
const USD_TO_COP = Number(import.meta.env.VITE_AUTOMATION_ROI_USD_TO_COP || 4000)

const currencyConfig = {
  COP: { locale: 'es-CO', currency: 'COP', symbol: '$', rateToCop: 1 },
  USD: { locale: 'en-US', currency: 'USD', symbol: 'US$', rateToCop: USD_TO_COP }
}

const BASE_SETUP_PRICE_COP = 2800000
const USER_COST_BY_RANGE_COP = [
  { maxUsers: 3, value: 180000 },
  { maxUsers: 10, value: 120000 },
  { maxUsers: 20, value: 95000 },
  { maxUsers: Number.POSITIVE_INFINITY, value: 70000 }
]

const AUTOMATION_COMPLEXITY_FACTORS = {
  0.25: 1,
  0.5: 1.35,
  0.75: 1.8
}

const stepDefinitions = [
  { title: 'Tamaño de empresa', subtitle: 'Ajustamos el diagnóstico a tu escala real.', icon: Users },
  { title: 'Proceso a validar', subtitle: 'Cuanto mejor entendamos el caso, mejor será la lectura.', icon: Workflow },
  { title: 'Usuarios del sistema', subtitle: 'Define cuántas personas usarán activamente la solución.', icon: BriefcaseBusiness },
  { title: 'Tiempo perdido', subtitle: 'Traducimos la fricción operativa en horas de trabajo.', icon: Clock3 },
  { title: 'Costo laboral', subtitle: 'Usamos el salario promedio para calcular el impacto económico.', icon: Coins },
  { title: 'Inversión inicial', subtitle: 'Ingresa el valor que quieres usar como referencia para el proyecto.', icon: Coins },
  { title: 'Potencial de automatización', subtitle: 'Elige un escenario conservador, medio o alto.', icon: Bot }
]

const automationOptions = [
  { value: 0.25, label: '25%', note: 'Escenario conservador' },
  { value: 0.5, label: '50%', note: 'Escenario medio' },
  { value: 0.75, label: '75%', note: 'Escenario alto' }
]

const roiHorizonOptions = [
  { value: 0.25, label: '3', unit: 'meses', description: '3 meses' },
  { value: 0.5, label: '6', unit: 'meses', description: '6 meses' },
  { value: 1, label: '1', unit: 'año', description: '1 año' },
  { value: 2, label: '2', unit: 'años', description: '2 años' },
  { value: 3, label: '3', unit: 'años', description: '3 años' },
  { value: 5, label: '5', unit: 'años', description: '5 años' }
]

const initialWizard = {
  employees: 12,
  currency: 'COP',
  businessIdea: '',
  participants: 3,
  hoursLostPerWeek: 8,
  averageSalaryMonthly: '',
  estimatedInvestment: '',
  automationRate: 0.5,
  roiYears: 3
}

const initialLead = {
  fullName: '',
  company: '',
  role: '',
  email: '',
  phone: '',
  consentAccepted: false,
  website: ''
}

const toCanonicalSlug = (region) => `/${region}/soluciones/automatizacion-procesos/roi-automatizacion`

const parseUtm = (search) => {
  const params = new URLSearchParams(search)
  return {
    utmSource: params.get('utm_source') || '',
    utmMedium: params.get('utm_medium') || '',
    utmCampaign: params.get('utm_campaign') || '',
    utmTerm: params.get('utm_term') || '',
    utmContent: params.get('utm_content') || ''
  }
}

const safeEventPayload = (value) => {
  try {
    return JSON.stringify(value)
  } catch {
    return '{}'
  }
}

const upsertMeta = (attribute, key, content) => {
  if (!content) return
  let element = document.head.querySelector(`meta[${attribute}="${key}"]`)
  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, key)
    document.head.appendChild(element)
  }
  element.setAttribute('content', content)
}

const upsertCanonical = (href) => {
  let canonical = document.head.querySelector('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }
  canonical.setAttribute('href', href)
}

const upsertJsonLd = (json) => {
  const id = 'automation-roi-jsonld'
  let script = document.getElementById(id)
  if (!script) {
    script = document.createElement('script')
    script.id = id
    script.type = 'application/ld+json'
    document.head.appendChild(script)
  }
  script.textContent = JSON.stringify(json)
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)
const toCop = (amount, currency) => Number(amount || 0) * (currencyConfig[currency] || currencyConfig.COP).rateToCop
const fromCop = (amountCop, currency) => amountCop / (currencyConfig[currency] || currencyConfig.COP).rateToCop
const easeInOutCubic = (value) => (value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2)
const smoothScrollToElement = (element, offset = 96, duration = 900) => {
  if (!element) return
  const startY = window.scrollY
  const targetY = Math.max(0, element.getBoundingClientRect().top + window.scrollY - offset)
  const distance = targetY - startY
  if (Math.abs(distance) < 4) {
    window.scrollTo({ top: targetY, behavior: 'auto' })
    return
  }

  const startTime = performance.now()
  const frame = (currentTime) => {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    const easedProgress = easeInOutCubic(progress)
    window.scrollTo({ top: startY + (distance * easedProgress), behavior: 'auto' })
    if (progress < 1) window.requestAnimationFrame(frame)
  }

  window.requestAnimationFrame(frame)
}
const formatMoney = (amount, currency, digits = 0) => new Intl.NumberFormat(currencyConfig[currency].locale, {
  style: 'currency',
  currency: currencyConfig[currency].currency,
  maximumFractionDigits: digits,
  minimumFractionDigits: digits
}).format(Number(amount || 0))
const formatCompactMoney = (amount, currency) => new Intl.NumberFormat(currencyConfig[currency].locale, {
  style: 'currency',
  currency: currencyConfig[currency].currency,
  notation: 'compact',
  maximumFractionDigits: 1
}).format(Number(amount || 0))
const formatNumber = (amount, digits = 0) => new Intl.NumberFormat('es-CO', {
  maximumFractionDigits: digits,
  minimumFractionDigits: digits
}).format(Number(amount || 0))

const getUserCostMultiplier = (users) => (
  USER_COST_BY_RANGE_COP.find((range) => users <= range.maxUsers)?.value || USER_COST_BY_RANGE_COP[USER_COST_BY_RANGE_COP.length - 1].value
)

const getAutomationComplexityFactor = (automationRate) => (
  AUTOMATION_COMPLEXITY_FACTORS[automationRate] || 1
)

const formatRoiHorizon = (value) => {
  const option = roiHorizonOptions.find((item) => item.value === value)
  if (option) return option.description
  return `${formatNumber(value, value < 1 ? 2 : 0)} años`
}

const buildRecommendation = (metrics) => {
  const paybackMonthsText = metrics.paybackMonths ? `${formatNumber(metrics.paybackMonths, 1)} meses` : 'un periodo por validar'
  const paybackYearsText = metrics.paybackYears ? `${formatNumber(metrics.paybackYears, 1)} años` : 'un periodo por validar'
  const roiText = metrics.roiProjected !== null ? `${formatNumber(metrics.roiProjected, 0)}%` : 'un retorno por validar'

  return `Con base en los datos ingresados, la automatización de este proceso podría recuperar la inversión en aproximadamente ${paybackMonthsText} (${paybackYearsText}) y generar un retorno estimado de ${roiText} en ${formatRoiHorizon(metrics.roiYears)}.`
}

const metricCards = (metrics, currency) => [
  ['Ahorro mensual estimado', formatMoney(metrics.monthlySavings, currency), 'text-emerald-700'],
  ['Ahorro anual estimado', formatMoney(metrics.annualSavings, currency), 'text-emerald-700'],
  ['Eficiencia potencial', `${formatNumber(metrics.efficiencyPotential, 0)}%`, 'text-sky-700'],
  ['Payback mensual', metrics.paybackMonths ? `${formatNumber(metrics.paybackMonths, 1)} meses` : 'Por validar', 'text-amber-700'],
  ['Payback anual', metrics.paybackYears ? `${formatNumber(metrics.paybackYears, 1)} años` : 'Por validar', 'text-amber-700']
]

export default function AutomationRoiLanding() {
  const { register, login, user, isAuthenticated } = useAuth()
  const { region, category, product } = useParams()
  const location = useLocation()
  const [wizard, setWizard] = useState(initialWizard)
  const [lead, setLead] = useState(initialLead)
  const [stepIndex, setStepIndex] = useState(0)
  const [wizardErrors, setWizardErrors] = useState({})
  const [leadErrors, setLeadErrors] = useState({})
  const [hasUnlockedDashboard, setHasUnlockedDashboard] = useState(false)
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false)
  const [authMode, setAuthMode] = useState('register')
  const [resumeAfterAccess, setResumeAfterAccess] = useState(false)
  const [account, setAccount] = useState({ password: '', confirmPassword: '' })
  const [submitState, setSubmitState] = useState({ type: '', message: '' })
  const [sending, setSending] = useState(false)
  const [sessionId, setSessionId] = useState(() => getStoredSessionId())
  const [turnstileToken, setTurnstileToken] = useState('')
  const wizardStartedRef = useRef(false)
  const dashboardTrackedRef = useRef(false)
  const accessFormStartedRef = useRef(false)
  const wizardSectionRef = useRef(null)
  const dashboardSectionRef = useRef(null)
  const turnstileContainerRef = useRef(null)
  const turnstileWidgetIdRef = useRef(null)

  const normalizedRegion = (region || 'latam').toLowerCase()
  const normalizedCategory = (category || 'automatizacion-procesos').toLowerCase()
  const normalizedProduct = (product || 'roi-automatizacion').toLowerCase()
  const isValidRoute = normalizedCategory === 'automatizacion-procesos' && normalizedProduct === 'roi-automatizacion'
  const landingSlug = toCanonicalSlug(normalizedRegion)
  const utm = useMemo(() => parseUtm(location.search), [location.search])

  const metrics = useMemo(() => {
    const salaryCop = toCop(wizard.averageSalaryMonthly, wizard.currency)
    const hourlyCostCop = salaryCop / 160
    const monthlyManualCostCop = wizard.hoursLostPerWeek * 4 * hourlyCostCop * wizard.participants
    const monthlySavingsCop = monthlyManualCostCop * wizard.automationRate
    const annualSavingsCop = monthlySavingsCop * 12
    const userCostMultiplierCop = getUserCostMultiplier(wizard.participants)
    const complexityFactor = getAutomationComplexityFactor(wizard.automationRate)
    const suggestedInvestmentCop = BASE_SETUP_PRICE_COP + (wizard.participants * userCostMultiplierCop * complexityFactor)
    const manualInvestmentCop = toCop(wizard.estimatedInvestment, wizard.currency)
    const implementationCostCop = manualInvestmentCop > 0 ? manualInvestmentCop : suggestedInvestmentCop
    const paybackMonths = monthlySavingsCop > 0 ? implementationCostCop / monthlySavingsCop : null
    const paybackYears = paybackMonths !== null ? paybackMonths / 12 : null
    const roiProjected = implementationCostCop > 0
      ? ((((annualSavingsCop * wizard.roiYears) - implementationCostCop) / implementationCostCop) * 100)
      : null
    return {
      hourlyCost: fromCop(hourlyCostCop, wizard.currency),
      monthlyManualCost: fromCop(monthlyManualCostCop, wizard.currency),
      monthlySavings: fromCop(monthlySavingsCop, wizard.currency),
      annualSavings: fromCop(annualSavingsCop, wizard.currency),
      implementationCost: fromCop(implementationCostCop, wizard.currency),
      suggestedInvestment: fromCop(suggestedInvestmentCop, wizard.currency),
      manualInvestment: fromCop(manualInvestmentCop, wizard.currency),
      baseSetupCost: fromCop(BASE_SETUP_PRICE_COP, wizard.currency),
      usersInvestment: fromCop(wizard.participants * userCostMultiplierCop, wizard.currency),
      complexityFactor,
      potentialOperationalCost: fromCop(monthlyManualCostCop - monthlySavingsCop, wizard.currency),
      recoveredHoursMonthly: wizard.hoursLostPerWeek * 4 * wizard.participants * wizard.automationRate,
      paybackMonths,
      paybackYears,
      roiProjected,
      roiYears: wizard.roiYears,
      efficiencyPotential: wizard.automationRate * 100
    }
  }, [wizard])

  useEffect(() => {
    if (!isValidRoute) return
    const canonicalUrl = `${window.location.origin}${landingSlug}`
    const title = 'Calculadora ROI de automatización gratis para empresas | AppEmprendedor'
    const description = 'Calculadora ROI de automatización gratis para empresas pequeñas y en crecimiento. Estima ahorro, payback y retorno por automatizar procesos y tareas manuales en COP o USD.'
    document.title = title
    upsertCanonical(canonicalUrl)
    upsertMeta('name', 'description', description)
    upsertMeta('property', 'og:title', title)
    upsertMeta('property', 'og:description', description)
    upsertMeta('property', 'og:type', 'website')
    upsertMeta('property', 'og:url', canonicalUrl)
    upsertMeta('name', 'twitter:card', 'summary_large_image')
    upsertMeta('name', 'twitter:title', title)
    upsertMeta('name', 'twitter:description', description)
    upsertJsonLd({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'Calculadora gratis de ROI de Automatización para Empresas de 2 a 100 Empleados',
      description,
      url: canonicalUrl,
      inLanguage: 'es',
      about: [
        'calculadora ROI automatización',
        'automatización de procesos para empresas pequeñas',
        'ahorro por automatizar procesos',
        'ROI de automatización empresarial'
      ],
      publisher: { '@type': 'Organization', name: 'AppEmprendedor' }
    })
  }, [isValidRoute, landingSlug])

  useEffect(() => {
    if (!isValidRoute || !TURNSTILE_SITE_KEY || !isRegistrationModalOpen) return undefined
    let disposed = false
    const renderWidget = () => {
      if (disposed || !turnstileContainerRef.current || !window.turnstile || turnstileWidgetIdRef.current !== null) return
      turnstileWidgetIdRef.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        'expired-callback': () => setTurnstileToken(''),
        'error-callback': () => setTurnstileToken('')
      })
    }
    if (window.turnstile) {
      renderWidget()
      return () => { disposed = true }
    }
    let script = document.querySelector('script[data-turnstile="true"]')
    if (!script) {
      script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.setAttribute('data-turnstile', 'true')
      document.head.appendChild(script)
    }
    script.addEventListener('load', renderWidget)
    return () => {
      disposed = true
      script.removeEventListener('load', renderWidget)
      if (window.turnstile && turnstileWidgetIdRef.current !== null && typeof window.turnstile.remove === 'function') {
        window.turnstile.remove(turnstileWidgetIdRef.current)
      }
      turnstileWidgetIdRef.current = null
    }
  }, [isValidRoute, isRegistrationModalOpen])

  useEffect(() => {
    if (!isRegistrationModalOpen) return undefined
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isRegistrationModalOpen])

  useEffect(() => {
    if (!isValidRoute) return
    const currentSessionId = getStoredSessionId()
    const anonymousId = getOrCreateAnonymousId()
    const run = async () => {
      try {
        const visit = await registerLandingVisit({
          landingSlug,
          productCode: PRODUCT_CODE,
          sessionId: currentSessionId || undefined,
          anonymousId,
          referrer: document.referrer || undefined,
          ...utm
        })
        if (visit?.sessionId) {
          setStoredSessionId(visit.sessionId)
          setSessionId(visit.sessionId)
        }
        await registerLandingEvent({
          landingSlug,
          productCode: PRODUCT_CODE,
          eventName: 'page_view',
          sessionId: visit?.sessionId || currentSessionId || 'anonymous',
          payloadJson: safeEventPayload({ path: location.pathname }),
          ...utm
        })
      } catch (error) {
        console.error('No se pudo registrar tracking inicial', error)
      }
    }
    run()
  }, [isValidRoute, landingSlug, location.pathname, utm])

  const trackEvent = async (eventName, payload = {}) => {
    try {
      await registerLandingEvent({
        landingSlug,
        productCode: PRODUCT_CODE,
        eventName,
        sessionId: sessionId || getStoredSessionId() || 'anonymous',
        payloadJson: safeEventPayload(payload),
        ...utm
      })
    } catch (error) {
      console.error(`Error registrando evento ${eventName}`, error)
    }
  }

  useEffect(() => {
    if (!hasUnlockedDashboard || dashboardTrackedRef.current) return
    dashboardTrackedRef.current = true
    trackEvent('dashboard_view', {
      monthlySavings: metrics.monthlySavings,
      annualSavings: metrics.annualSavings,
      paybackMonths: metrics.paybackMonths
    })
  }, [hasUnlockedDashboard, metrics])

  useEffect(() => {
    if (!user) return
    setLead((prev) => ({
      ...prev,
      fullName: prev.fullName || [user.first_name, user.last_name].filter(Boolean).join(' ').trim(),
      email: prev.email || user.email || ''
    }))
  }, [user])

  const startWizard = () => {
    if (!isAuthenticated) {
      setResumeAfterAccess(true)
      setAuthMode('login')
      setSubmitState({ type: '', message: '' })
      setLeadErrors({})
      setIsRegistrationModalOpen(true)
      trackEvent('access_gate_open', { source: 'start_wizard' })
      return
    }
    wizardSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    if (wizardStartedRef.current) return
    wizardStartedRef.current = true
    trackEvent('wizard_start', { source: 'hero_cta' })
  }

  const updateWizardField = (field, value) => {
    setWizard((prev) => ({ ...prev, [field]: value }))
    if (wizardErrors[field]) setWizardErrors((prev) => ({ ...prev, [field]: '' }))
  }

  const updateCurrency = (nextCurrency) => {
    setWizard((prev) => prev.currency === nextCurrency ? prev : {
      ...prev,
      currency: nextCurrency,
      averageSalaryMonthly: prev.averageSalaryMonthly
        ? Math.round(fromCop(toCop(prev.averageSalaryMonthly, prev.currency), nextCurrency))
        : ''
    })
  }

  const validateCurrentStep = () => {
    const errors = {}
    if (stepIndex === 0 && (wizard.employees < 2 || wizard.employees > 100)) errors.employees = 'Ingresa entre 2 y 100 empleados.'
    if (stepIndex === 1) {
      if (!wizard.businessIdea.trim()) errors.businessIdea = 'Describe la idea o proceso que deseas validar.'
      else if (wizard.businessIdea.trim().length < 10) errors.businessIdea = 'Agrega un poco más de contexto.'
    }
    if (stepIndex === 2) {
      if (wizard.participants < 1) errors.participants = 'Debe haber al menos un usuario del sistema.'
      if (wizard.participants > wizard.employees) errors.participants = 'No puede superar el total de empleados.'
    }
    if (stepIndex === 3 && wizard.hoursLostPerWeek <= 0) errors.hoursLostPerWeek = 'Ingresa las horas perdidas por semana.'
    if (stepIndex === 4 && (!wizard.averageSalaryMonthly || Number(wizard.averageSalaryMonthly) <= 0)) errors.averageSalaryMonthly = 'Ingresa un salario promedio mensual válido.'
    if (stepIndex === 5 && (!wizard.estimatedInvestment || Number(wizard.estimatedInvestment) <= 0)) errors.estimatedInvestment = 'Ingresa una inversión inicial válida.'
    setWizardErrors(errors)
    return Object.keys(errors).length === 0
  }

  const goNext = () => {
    if (!wizardStartedRef.current) {
      wizardStartedRef.current = true
      trackEvent('wizard_start', { source: 'step_navigation' })
    }
    if (!validateCurrentStep()) return
    if (stepIndex === STEP_COUNT - 1) {
      unlockDashboardPreview('wizard_last_step')
      return
    }
    const next = clamp(stepIndex + 1, 0, STEP_COUNT - 1)
    setStepIndex(next)
    trackEvent('wizard_step', { fromStep: stepIndex + 1, toStep: next + 1, stepTitle: stepDefinitions[next].title })
  }

  const goPrevious = () => setStepIndex((prev) => clamp(prev - 1, 0, STEP_COUNT - 1))

  const updateLeadField = (field, value) => {
    setLead((prev) => ({ ...prev, [field]: value }))
    if (leadErrors[field]) setLeadErrors((prev) => ({ ...prev, [field]: '' }))
  }

  const updateAccountField = (field, value) => {
    setAccount((prev) => ({ ...prev, [field]: value }))
    if (leadErrors[field]) setLeadErrors((prev) => ({ ...prev, [field]: '' }))
  }

  const openRegistrationModal = (mode = null, resume = false) => {
    setSubmitState({ type: '', message: '' })
    setLeadErrors({})
    setAuthMode(mode || (isAuthenticated ? 'login' : 'register'))
    setResumeAfterAccess(resume)
    setIsRegistrationModalOpen(true)
    trackEvent('access_gate_open', { source: 'manual_open' })
  }

  const closeRegistrationModal = () => {
    setIsRegistrationModalOpen(false)
    setResumeAfterAccess(false)
    setTurnstileToken('')
    if (window.turnstile && turnstileWidgetIdRef.current !== null) {
      if (typeof window.turnstile.remove === 'function') {
        window.turnstile.remove(turnstileWidgetIdRef.current)
      } else {
        window.turnstile.reset(turnstileWidgetIdRef.current)
      }
    }
    turnstileWidgetIdRef.current = null
  }

  const unlockDashboardPreview = (source = 'preview_button') => {
    setHasUnlockedDashboard(true)
    trackEvent('dashboard_unlock', { stepReached: stepIndex + 1, authenticated: isAuthenticated, source })
  }

  const startAccessFormTracking = () => {
    if (accessFormStartedRef.current) return
    accessFormStartedRef.current = true
    trackEvent('form_start', { source: 'automationroi_access_form' })
  }

  useEffect(() => {
    if (!hasUnlockedDashboard) return
    const timeoutId = window.setTimeout(() => {
      smoothScrollToElement(dashboardSectionRef.current)
    }, 50)
    return () => window.clearTimeout(timeoutId)
  }, [hasUnlockedDashboard])

  const validateLead = () => {
    const errors = {}
    if (!isAuthenticated && authMode === 'register' && !lead.fullName.trim()) errors.fullName = 'Ingresa tu nombre completo.'
    if (!lead.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(lead.email.trim())) errors.email = 'Ingresa un correo electrónico válido.'
    if (!isAuthenticated) {
      if (!account.password) errors.password = authMode === 'login' ? 'Ingresa tu contraseña.' : 'Crea una contraseña.'
      else if (authMode === 'register' && (account.password.length < 6 || !/\d/.test(account.password))) errors.password = 'La contraseña debe tener mínimo 6 caracteres y un número.'
      if (authMode === 'register') {
        if (!account.confirmPassword) errors.confirmPassword = 'Confirma la contraseña.'
        else if (account.password !== account.confirmPassword) errors.confirmPassword = 'Las contraseñas no coinciden.'
      }
    }
    if (!lead.consentAccepted) errors.consentAccepted = 'Debes aceptar el tratamiento de datos.'
    if (!turnstileToken) errors.turnstileToken = 'Completa la verificacion anti-spam.'
    setLeadErrors(errors)
    return Object.keys(errors).length === 0
  }

  const submitLeadCapture = async () => {
    const resolvedFullName = lead.fullName.trim() || [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim() || lead.email.trim()
    const summaryLines = [
      `Proceso o idea: ${wizard.businessIdea.trim() || 'No especificado'}`,
      `Empleados: ${wizard.employees}`,
      `Usuarios del sistema: ${wizard.participants}`,
      `Horas perdidas por semana: ${wizard.hoursLostPerWeek}`,
      `Salario promedio mensual: ${wizard.averageSalaryMonthly || 'No especificado'} ${wizard.currency}`,
      `Inversión inicial ingresada: ${formatMoney(metrics.implementationCost, wizard.currency)}`,
      `Inversión sugerida de referencia: ${formatMoney(metrics.suggestedInvestment, wizard.currency)}`,
      `Factor de complejidad: ${formatNumber(metrics.complexityFactor, 2)}x`,
      `Automatización estimada: ${Math.round(wizard.automationRate * 100)}%`,
      `Horizonte ROI: ${formatRoiHorizon(wizard.roiYears)}`,
      `Ahorro mensual estimado: ${formatMoney(metrics.monthlySavings, wizard.currency)}`,
      `Ahorro anual estimado: ${formatMoney(metrics.annualSavings, wizard.currency)}`
    ]

    const response = await fetch('/api/leads', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        name: resolvedFullName,
        email: lead.email.trim(),
        company: lead.company.trim() || undefined,
        phone: lead.phone.trim() || undefined,
        role: lead.role.trim() || undefined,
        interest: 'Calculadora ROI de automatización',
        message: summaryLines.join('\n'),
        consentAccepted: lead.consentAccepted,
        website: lead.website,
        landingSlug,
        productCode: PRODUCT_CODE,
        sessionId: sessionId || getStoredSessionId() || undefined,
        turnstileToken,
        ...utm
      })
    })

    const payload = await response.json().catch(() => null)
    if (!response.ok) {
      throw new Error(payload?.message || 'No se pudo registrar la conversion de la landing.')
    }

    return payload
  }

  const submitLead = async (event) => {
    event.preventDefault()
    setSubmitState({ type: '', message: '' })
    trackEvent('access_attempt', { authMode, resumeAfterAccess })
    if (!validateLead()) return
    setSending(true)
    try {
      let authResult = null

      if (!isAuthenticated && authMode === 'register') {
        const nameParts = lead.fullName.trim().split(/\s+/).filter(Boolean)
        const firstName = nameParts.shift() || lead.fullName.trim()
        const lastName = nameParts.join(' ') || 'Usuario'

        authResult = await register({
          firstName,
          lastName,
          email: lead.email.trim(),
          password: account.password
        })
      }

      if (!isAuthenticated && authMode === 'login') {
        await login(lead.email.trim(), account.password)
        authResult = { loggedIn: true }
      }

      let leadCapture = null
      try {
        leadCapture = await submitLeadCapture()
        await trackEvent('form_submit_success', {
          leadId: leadCapture?.leadId || null,
          source: 'automationroi_access_modal'
        })
      } catch (captureError) {
        console.error('No se pudo registrar lead de ROI', captureError)
      }

      setSubmitState({
        type: 'success',
        message: authResult?.requiresEmailConfirmation
          ? 'Tu cuenta fue creada. Revisa tu correo para verificarla antes de iniciar sesión.'
          : authMode === 'login' || isAuthenticated
            ? 'Sesión iniciada. Ya puedes usar la calculadora.'
            : 'Tu cuenta fue creada. Ya puedes usar la calculadora.'
      })
      await trackEvent('access_success', { authMode, requiresEmailConfirmation: Boolean(authResult?.requiresEmailConfirmation) })
      setAccount({ password: '', confirmPassword: '' })
      setTurnstileToken('')
      accessFormStartedRef.current = false
      if (window.turnstile && turnstileWidgetIdRef.current !== null) window.turnstile.reset(turnstileWidgetIdRef.current)
      if (!authResult?.requiresEmailConfirmation) {
        closeRegistrationModal()
        if (resumeAfterAccess) {
          window.setTimeout(() => {
            wizardSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }, 50)
        }
      }
    } catch (error) {
      setSubmitState({ type: 'error', message: error?.message || 'No pudimos procesar tu solicitud en este momento.' })
    } finally {
      setSending(false)
    }
  }

  if (!isValidRoute) {
    return (
      <div className="min-h-screen bg-[#f4f3ef] text-slate-900 flex items-center justify-center px-6">
        <div className="max-w-xl text-center">
          <h1 className="text-3xl font-display">Landing no disponible</h1>
          <p className="mt-4 text-slate-600">La ruta solicitada no corresponde a la calculadora ROI de automatización.</p>
          <Link to="/latam/soluciones/automatizacion-procesos/roi-automatizacion" className="inline-flex mt-6 px-5 py-3 rounded-xl bg-slate-900 text-white font-semibold">Ir a la landing principal</Link>
        </div>
      </div>
    )
  }

  const currentStep = stepDefinitions[stepIndex]
  const CurrentStepIcon = currentStep.icon
  const progress = ((stepIndex + 1) / STEP_COUNT) * 100
  const lockedCards = metricCards(metrics, wizard.currency)
  const currentVsPotential = [
    ['Costo operativo mensual actual', metrics.monthlyManualCost, 'bg-rose-500/85'],
    ['Costo operativo potencial', metrics.potentialOperationalCost, 'bg-slate-300'],
    ['Ahorro mensual estimado', metrics.monthlySavings, 'bg-emerald-500/85']
  ]
  const maxComparisonValue = Math.max(...currentVsPotential.map(([, value]) => Number(value)), 1)

  return (
    <div className="min-h-screen bg-[#f5f2ec] text-slate-900">
      <style>{`
        @keyframes roiPulseGlow {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 18px 38px rgba(79, 70, 229, 0.14);
            opacity: 0.96;
          }
          50% {
            transform: scale(1.02);
            box-shadow: 0 24px 52px rgba(79, 70, 229, 0.22);
            opacity: 1;
          }
        }
      `}</style>
      <div className="fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-[420px] bg-[radial-gradient(circle_at_top,#e7ebe5,transparent_62%)]" />
        <div className="absolute right-0 top-28 h-72 w-72 rounded-full bg-emerald-100/70 blur-3xl" />
        <div className="absolute left-[-60px] top-16 h-72 w-72 rounded-full bg-stone-200/80 blur-3xl" />
      </div>

      <LandingMainHeader className="bg-[#f5f2ec]/90 border-slate-200/80" theme="light" />

      <main>
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-14">
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-12 items-start">
            <div>
              <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-300 bg-white/80 text-xs uppercase tracking-[0.24em] text-slate-600"><Sparkles className="w-3.5 h-3.5" />Diagnóstico consultivo AppEmprendedor</p>
              <h1 className="mt-6 max-w-4xl text-4xl sm:text-5xl lg:text-6xl font-display leading-tight text-balance">Calculadora de ROI de Automatización para Empresas de 2 a 100 Empleados</h1>
              <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-700">Descubre cuánto dinero podrías ahorrar al reducir tareas manuales, reprocesos y tiempos improductivos.</p>
              <ul className="mt-8 space-y-3 text-sm sm:text-base text-slate-700">
                <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />Diagnóstico visual en pocos minutos</li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />Resultado ejecutivo en COP o USD</li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />Enfoque práctico para empresas pequeñas y en crecimiento</li>
              </ul>
              <div className="mt-9 flex flex-wrap gap-3">
                <button onClick={startWizard} className="px-6 py-3 rounded-2xl bg-slate-900 text-white font-semibold inline-flex items-center gap-2 shadow-[0_18px_40px_rgba(15,23,42,0.14)]">{isAuthenticated ? 'Comenzar diagnóstico' : 'Acceder para comenzar'} <ArrowRight className="w-4 h-4" /></button>
                <a href="#roi-intro" className="px-6 py-3 rounded-2xl border border-slate-300 bg-white/80 text-slate-700 font-semibold">Cómo se calcula</a>
              </div>
            </div>

            <aside className="rounded-[28px] border border-slate-200 bg-white/90 p-6 shadow-[0_24px_60px_rgba(15,23,42,0.08)]">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-slate-600">Señales de oportunidad</p>
                <span className="px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">ROI automatización empresarial</span>
              </div>
              <div className="mt-6 space-y-4">
                {[
                  ['Ahorro mensual estimado', formatCompactMoney(metrics.monthlySavings, wizard.currency), 'text-emerald-700'],
                  ['Costo operativo mensual actual', formatCompactMoney(metrics.monthlyManualCost, wizard.currency), 'text-rose-700'],
                  ['Payback mensual', metrics.paybackMonths ? `${formatNumber(metrics.paybackMonths, 1)} meses` : 'Por validar', 'text-sky-700']
                ].map(([label, value, tone]) => (
                  <div key={label} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4">
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">{label}</p>
                    <p className={`mt-2 text-2xl font-semibold ${tone}`}>{value}</p>
                  </div>
                ))}
              </div>
              <div className="mt-6 rounded-2xl bg-[#1f3b35] px-5 py-5 text-slate-50">
                <p className="text-sm font-semibold">Lectura rápida</p>
                <p className="mt-3 text-sm leading-6 text-slate-200">Si hoy varias personas repiten tareas administrativas o de seguimiento, esta calculadora te ayuda a traducir ese desgaste en ahorro posible.</p>
              </div>
            </aside>
          </div>
        </section>

        <section className="border-y border-slate-200 bg-white/70">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid md:grid-cols-3 gap-4">
            {[
              [ShieldCheck, 'Herramienta consultiva seria', 'Pensada para gerencia, operaciones y crecimiento.'],
              [LayoutDashboard, 'Dashboard ejecutivo al final', 'Visualiza ahorro, eficiencia, costo actual y recomendación.'],
              [Phone, 'Acceso requerido para iniciar', 'Para usar la calculadora debes ingresar con tu cuenta o crear una nueva.']
            ].map(([Icon, title, text]) => (
              <article key={title} className="rounded-2xl border border-slate-200 bg-[#faf8f4] px-5 py-5">
                <Icon className="w-5 h-5 text-slate-900" />
                <h2 className="mt-3 text-lg font-semibold">{title}</h2>
                <p className="mt-2 text-sm leading-6 text-slate-600">{text}</p>
              </article>
            ))}
          </div>
        </section>

        {!isAuthenticated && <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12"><div className="rounded-[30px] border border-slate-200 bg-white/90 p-6 sm:p-8 shadow-[0_24px_60px_rgba(15,23,42,0.08)]"><p className="text-xs uppercase tracking-[0.2em] text-slate-500">Antes de comenzar</p><h2 className="mt-3 text-3xl font-display text-slate-900">Accede para usar la calculadora ROI</h2><p className="mt-4 max-w-3xl text-sm sm:text-base leading-7 text-slate-600">El proceso funciona así: primero inicias sesión o creas tu cuenta, luego completas el diagnóstico paso a paso y al final ves el informe con ahorro, payback y ROI estimado. El acceso previo nos permite guardar el avance y mantener el informe asociado a tu usuario.</p><div className="mt-8 grid gap-4 md:grid-cols-3">{[['1', 'Accede o regístrate', 'Usa una cuenta existente o crea una nueva en menos de un minuto.'], ['2', 'Completa el diagnóstico', 'Ingresa tamaño, proceso, tiempo perdido, salario, inversión inicial y horizonte ROI.'], ['3', 'Revisa el informe', 'Obtendrás una estimación orientativa con métricas ejecutivas.']].map(([step, title, text]) => (<article key={step} className="rounded-2xl border border-slate-200 bg-[#faf8f4] px-5 py-5"><p className="text-sm font-semibold text-slate-500">Paso {step}</p><h3 className="mt-2 text-lg font-semibold text-slate-900">{title}</h3><p className="mt-2 text-sm leading-6 text-slate-600">{text}</p></article>))}</div><div className="mt-8 flex flex-wrap gap-3"><button type="button" onClick={() => openRegistrationModal('login', true)} className="rounded-2xl bg-slate-900 px-6 py-3 font-semibold text-white inline-flex items-center gap-2">Iniciar sesión <ArrowRight className="w-4 h-4" /></button><button type="button" onClick={() => openRegistrationModal('register', true)} className="rounded-2xl border border-slate-300 bg-white px-6 py-3 font-semibold text-slate-700">Crear cuenta</button></div></div></section>}

        {isAuthenticated && <section id="roi-wizard" ref={wizardSectionRef} className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid gap-8 items-start">
            <div className="rounded-[30px] border border-slate-200 bg-white/90 p-6 sm:p-8 shadow-[0_24px_60px_rgba(15,23,42,0.08)]">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.2em] text-slate-500">Wizard de diagnóstico</p>
                  <h2 className="mt-2 text-2xl sm:text-3xl font-display">Calculadora ROI automatización</h2>
                </div>
                <div className="text-sm text-slate-500">Paso {stepIndex + 1} de {STEP_COUNT}</div>
              </div>
              <div className="mt-6">
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-slate-900 transition-all duration-300" style={{ width: `${progress}%` }} /></div>
                <div className="mt-4 grid grid-cols-7 gap-2">
                  {stepDefinitions.map((step, index) => {
                    const StepIcon = step.icon
                    const active = index === stepIndex
                    const done = index < stepIndex
                    return <div key={step.title} className={`rounded-2xl border px-3 py-3 text-center ${active ? 'border-slate-900 bg-slate-900 text-white' : done ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-slate-50 text-slate-500'}`}><StepIcon className="w-4 h-4 mx-auto" /></div>
                  })}
                </div>
              </div>
              <div className="mt-8 rounded-[28px] border border-slate-200 bg-[#fbfaf7] p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-slate-900 text-white flex items-center justify-center shrink-0"><CurrentStepIcon className="w-5 h-5" /></div>
                  <div><h3 className="text-2xl font-display">{currentStep.title}</h3><p className="mt-2 text-sm leading-6 text-slate-600">{currentStep.subtitle}</p></div>
                </div>
                {stepIndex === 0 && <div className="mt-8 space-y-6"><div><div className="flex items-center justify-between"><label className="text-sm font-medium text-slate-700">Cantidad de empleados</label><span className="text-lg font-semibold">{wizard.employees}</span></div><input type="range" min="2" max="100" value={wizard.employees} onChange={(event) => updateWizardField('employees', Number(event.target.value))} className="mt-3 w-full accent-slate-900" />{wizardErrors.employees && <p className="mt-2 text-sm text-rose-600">{wizardErrors.employees}</p>}</div><div className="grid sm:grid-cols-2 gap-3">{Object.keys(currencyConfig).map((code) => (<button key={code} type="button" onClick={() => updateCurrency(code)} className={`rounded-2xl border px-4 py-4 text-left ${wizard.currency === code ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700'}`}><div className="flex items-center justify-between"><span className="text-lg font-semibold">{code}</span><Coins className="w-4 h-4" /></div><p className={`mt-2 text-sm ${wizard.currency === code ? 'text-slate-200' : 'text-slate-500'}`}>Visualiza tus cifras en {code === 'COP' ? 'pesos colombianos' : 'dólares'}.</p></button>))}</div></div>}
                {stepIndex === 1 && <div className="mt-8"><label htmlFor="businessIdea" className="text-sm font-medium text-slate-700">¿Qué idea de negocio, proceso o mejora quieres validar?</label><p className="mt-2 text-sm text-slate-500">Esto nos ayuda a personalizar mejor el diagnóstico.</p><textarea id="businessIdea" value={wizard.businessIdea} maxLength={280} rows={5} onChange={(event) => updateWizardField('businessIdea', event.target.value)} className="mt-4 w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10" placeholder="Ejemplo: seguimiento comercial, aprobaciones internas, soporte por WhatsApp, cargue manual de datos." /><div className="mt-2 flex items-center justify-between text-xs text-slate-500"><span>Se usará en el resumen final.</span><span>{wizard.businessIdea.length}/280</span></div>{wizardErrors.businessIdea && <p className="mt-2 text-sm text-rose-600">{wizardErrors.businessIdea}</p>}</div>}
                {stepIndex === 2 && <div className="mt-8"><div className="flex items-center justify-between"><label className="text-sm font-medium text-slate-700">¿Cuántas personas usarán el sistema?</label><span className="text-lg font-semibold">{wizard.participants}</span></div><input type="range" min="1" max={wizard.employees} value={wizard.participants} onChange={(event) => updateWizardField('participants', Number(event.target.value))} className="mt-3 w-full accent-slate-900" /><div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-4 text-sm text-slate-600">La referencia de inversión se calculará con <span className="font-semibold text-slate-900">{wizard.participants} usuarios activos</span>, equivalente al {formatNumber((wizard.participants / wizard.employees) * 100, 0)}% de la empresa.</div>{wizardErrors.participants && <p className="mt-3 text-sm text-rose-600">{wizardErrors.participants}</p>}</div>}
                {stepIndex === 3 && <div className="mt-8"><div className="flex items-center justify-between"><label className="text-sm font-medium text-slate-700">Horas perdidas por semana</label><span className="text-lg font-semibold">{wizard.hoursLostPerWeek} h</span></div><input type="range" min="1" max="40" value={wizard.hoursLostPerWeek} onChange={(event) => updateWizardField('hoursLostPerWeek', Number(event.target.value))} className="mt-3 w-full accent-slate-900" /><div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4 text-sm text-amber-900">Eso equivale a cerca de <span className="font-semibold">{formatNumber(wizard.hoursLostPerWeek * 4 * wizard.participants, 0)} horas al mes</span> sostenidas por el equipo.</div>{wizardErrors.hoursLostPerWeek && <p className="mt-3 text-sm text-rose-600">{wizardErrors.hoursLostPerWeek}</p>}</div>}
                {stepIndex === 4 && <div className="mt-8"><label htmlFor="salaryInput" className="text-sm font-medium text-slate-700">Salario promedio mensual de las personas involucradas</label><div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3"><span className="text-sm font-semibold text-slate-500">{currencyConfig[wizard.currency].symbol}</span><input id="salaryInput" type="number" min="0" value={wizard.averageSalaryMonthly} onChange={(event) => updateWizardField('averageSalaryMonthly', event.target.value)} className="w-full bg-transparent text-lg outline-none" placeholder={wizard.currency === 'COP' ? '3500000' : '950'} /></div><div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-4"><p className="text-xs uppercase tracking-[0.16em] text-slate-500">Costo hora estimado</p><p className="mt-2 text-xl font-semibold">{formatMoney(metrics.hourlyCost, wizard.currency)}</p></div>{wizardErrors.averageSalaryMonthly && <p className="mt-3 text-sm text-rose-600">{wizardErrors.averageSalaryMonthly}</p>}</div>}
                {stepIndex === 5 && <div className="mt-8 space-y-5"><div><label htmlFor="estimatedInvestment" className="text-sm font-medium text-slate-700">¿Cuál es la inversión inicial que quieres evaluar?</label><p className="mt-2 text-sm text-slate-500">Puedes ingresar tu valor directamente o tomar como referencia la estimación sugerida por la calculadora.</p><div className="mt-4 rounded-2xl border border-slate-200 bg-white px-4 py-3 flex items-center gap-3"><span className="text-sm font-semibold text-slate-500">{currencyConfig[wizard.currency].symbol}</span><input id="estimatedInvestment" type="number" min="0" value={wizard.estimatedInvestment} onChange={(event) => updateWizardField('estimatedInvestment', event.target.value)} className="w-full bg-transparent text-lg outline-none" placeholder={wizard.currency === 'COP' ? '4800000' : '1200'} /></div>{wizardErrors.estimatedInvestment && <p className="mt-3 text-sm text-rose-600">{wizardErrors.estimatedInvestment}</p>}</div><div className="rounded-2xl border border-slate-200 bg-[#faf8f4] px-4 py-4 text-sm text-slate-600"><p className="text-xs uppercase tracking-[0.16em] text-slate-500">Referencia sugerida</p><div className="mt-3 space-y-2"><div className="flex items-center justify-between gap-4"><span>Base de implementación</span><span className="font-semibold text-slate-900">{formatMoney(metrics.baseSetupCost, wizard.currency)}</span></div><div className="flex items-center justify-between gap-4"><span>Usuarios del sistema</span><span className="font-semibold text-slate-900">{formatMoney(metrics.usersInvestment, wizard.currency)}</span></div><div className="flex items-center justify-between gap-4"><span>Factor de complejidad</span><span className="font-semibold text-slate-900">{formatNumber(metrics.complexityFactor, 2)}x</span></div><div className="flex items-center justify-between gap-4 border-t border-slate-200 pt-2"><span>Valor sugerido</span><span className="text-base font-semibold text-slate-900">{formatMoney(metrics.suggestedInvestment, wizard.currency)}</span></div></div><p className="mt-3 text-xs leading-5 text-slate-500">Este valor es una referencia para ayudarte a definir la inversión inicial del análisis.</p><button type="button" onClick={() => updateWizardField('estimatedInvestment', Math.round(metrics.suggestedInvestment))} className="mt-4 rounded-xl border border-slate-300 bg-white px-4 py-2 font-semibold text-slate-700">Usar valor sugerido</button></div></div>}
                {stepIndex === 6 && <div className="mt-8 space-y-6"><div className="space-y-3">{automationOptions.map((option) => (<button key={option.value} type="button" onClick={() => updateWizardField('automationRate', option.value)} className={`w-full rounded-2xl border px-5 py-5 text-left ${wizard.automationRate === option.value ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700'}`}><div className="flex items-start justify-between gap-4"><div><p className="text-lg font-semibold">{option.label} de reducción estimada</p><p className={`mt-2 text-sm ${wizard.automationRate === option.value ? 'text-slate-200' : 'text-slate-500'}`}>{option.note}</p></div><Bot className="w-5 h-5 shrink-0" /></div></button>))}</div><div><p className="text-sm font-medium text-slate-700">Horizonte para calcular ROI</p><div className="mt-3 grid grid-cols-3 sm:grid-cols-6 gap-3">{roiHorizonOptions.map((option) => (<button key={option.value} type="button" onClick={() => updateWizardField('roiYears', option.value)} className={`rounded-2xl border px-3 py-4 text-center ${wizard.roiYears === option.value ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700'}`}><p className="text-lg font-semibold">{option.label}</p><p className={`mt-1 text-xs ${wizard.roiYears === option.value ? 'text-slate-200' : 'text-slate-500'}`}>{option.unit}</p></button>))}</div></div></div>}
              </div>
              <div className="mt-8 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3">
                <button type="button" onClick={goPrevious} disabled={stepIndex === 0} className="px-4 py-3 rounded-2xl border border-slate-300 text-slate-700 font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40"><ChevronLeft className="w-4 h-4" />Atrás</button>
                <button type="button" onClick={goNext} className="px-5 py-3 rounded-2xl bg-slate-900 text-white font-semibold inline-flex items-center justify-center gap-2">{stepIndex === STEP_COUNT - 1 ? 'Ver resumen previo' : 'Siguiente'}<ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>

            {submitState.message && !isRegistrationModalOpen && <div className={`rounded-[24px] border px-4 py-4 text-sm shadow-[0_20px_40px_rgba(15,23,42,0.08)] ${submitState.type === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>{submitState.message}</div>}
          </div>
        </section>}

        {isRegistrationModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-6">
            <button type="button" aria-label="Cerrar registro" onClick={closeRegistrationModal} className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm" />
            <div className="relative z-10 max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-[32px] border border-[#d8dfd2] bg-[linear-gradient(155deg,#f6f2ea_0%,#eef3ec_46%,#f9faf7_100%)] p-6 sm:p-8 text-slate-900 shadow-[0_30px_80px_rgba(15,23,42,0.24)]">
              <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[32px]">
                <div className="absolute -top-16 right-0 h-48 w-48 rounded-full bg-emerald-100/70 blur-3xl" />
                <div className="absolute left-[-40px] bottom-0 h-44 w-44 rounded-full bg-stone-200/70 blur-3xl" />
                <div className="absolute inset-x-0 top-0 h-28 bg-[linear-gradient(180deg,rgba(255,255,255,0.6),transparent)]" />
              </div>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.22em] text-emerald-700">Acceso a la calculadora</p>
                  <h2 className="mt-2 text-3xl font-display text-slate-900">{isAuthenticated ? 'Continúa con tu cuenta actual' : authMode === 'login' ? 'Inicia sesión para usar la calculadora' : 'Crea tu cuenta para usar la calculadora'}</h2>
                  <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">Necesitas una cuenta para empezar el diagnóstico y mantener el informe asociado a tu usuario dentro de la plataforma.</p>
                  <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-500">{isAuthenticated ? 'Ya detectamos una sesión activa. Puedes continuar directamente.' : authMode === 'login' ? 'Si ya eres usuario, inicia sesión y permanecerás dentro de la calculadora de ROI.' : 'Se crea una cuenta real y el software enviará un correo para verificarla antes de iniciar sesión.'}</p>
                </div>
                <button type="button" onClick={closeRegistrationModal} className="rounded-2xl border border-slate-300 bg-white/80 p-3 text-slate-600 hover:bg-white">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                {!isAuthenticated && (
                  <>
                    <button type="button" onClick={() => { setAuthMode('register'); setLeadErrors({}); setSubmitState({ type: '', message: '' }) }} className={`rounded-2xl px-4 py-2 text-sm font-semibold ${authMode === 'register' ? 'bg-slate-900 text-white' : 'border border-slate-300 bg-white/75 text-slate-700'}`}>Crear cuenta</button>
                    <button type="button" onClick={() => { setAuthMode('login'); setLeadErrors({}); setSubmitState({ type: '', message: '' }) }} className={`rounded-2xl px-4 py-2 text-sm font-semibold ${authMode === 'login' ? 'bg-slate-900 text-white' : 'border border-slate-300 bg-white/75 text-slate-700'}`}>Ya tengo cuenta</button>
                  </>
                )}
                {isAuthenticated && (
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-800">
                    Sesión detectada para {user?.email || 'usuario actual'}. Puedes continuar y ver el informe.
                  </div>
                )}
              </div>

              <div className="mt-6 grid gap-4 rounded-[24px] border border-slate-200 bg-white/70 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.7)] sm:grid-cols-2">
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Acceso</p>
                  <p className="mt-2 text-sm leading-6 text-slate-700">{isAuthenticated ? 'Sesión activa detectada' : authMode === 'login' ? 'Ingreso con cuenta existente' : 'Creación de cuenta nueva'}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.16em] text-slate-500">Luego del acceso</p>
                  <p className="mt-2 text-sm leading-6 text-slate-700">Podrás completar el diagnóstico y ver el informe ejecutivo en esta misma experiencia.</p>
                </div>
              </div>

              <form onSubmit={submitLead} className="mt-6" onFocus={startAccessFormTracking}>
                <div className="grid gap-6">
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Datos de contacto</p>
                    <div className="mt-3 grid sm:grid-cols-2 gap-3">
                      <div><div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 flex items-center gap-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]"><UserRound className="w-4 h-4 text-slate-400" /><input value={lead.fullName} onChange={(event) => updateLeadField('fullName', event.target.value)} placeholder="Nombre completo" className="w-full bg-transparent outline-none placeholder:text-slate-400" /></div>{leadErrors.fullName && <p className="mt-2 text-xs text-rose-600">{leadErrors.fullName}</p>}</div>
                      {!isAuthenticated && authMode === 'register' && <div><div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 flex items-center gap-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]"><Building2 className="w-4 h-4 text-slate-400" /><input value={lead.company} onChange={(event) => updateLeadField('company', event.target.value)} placeholder="Empresa (opcional)" className="w-full bg-transparent outline-none placeholder:text-slate-400" /></div></div>}
                      {!isAuthenticated && authMode === 'register' && <div><div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 flex items-center gap-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]"><BriefcaseBusiness className="w-4 h-4 text-slate-400" /><input value={lead.role} onChange={(event) => updateLeadField('role', event.target.value)} placeholder="Cargo (opcional)" className="w-full bg-transparent outline-none placeholder:text-slate-400" /></div></div>}
                      {!isAuthenticated && authMode === 'register' && <div><div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 flex items-center gap-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]"><Phone className="w-4 h-4 text-slate-400" /><input value={lead.phone} onChange={(event) => updateLeadField('phone', event.target.value)} placeholder="Teléfono (opcional)" className="w-full bg-transparent outline-none placeholder:text-slate-400" /></div></div>}
                      <div className="sm:col-span-2"><div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 flex items-center gap-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]"><Mail className="w-4 h-4 text-slate-400" /><input value={lead.email} onChange={(event) => updateLeadField('email', event.target.value)} type="email" placeholder="Correo electrónico" className="w-full bg-transparent outline-none placeholder:text-slate-400" disabled={isAuthenticated} /></div>{leadErrors.email && <p className="mt-2 text-xs text-rose-600">{leadErrors.email}</p>}</div>
                    </div>
                  </div>
                  {!isAuthenticated && (
                  <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-slate-500">Credenciales de acceso</p>
                    <div className="mt-3 grid sm:grid-cols-2 gap-3">
                      <div><div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 flex items-center gap-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]"><LockKeyhole className="w-4 h-4 text-slate-400" /><input value={account.password} onChange={(event) => updateAccountField('password', event.target.value)} type="password" placeholder={authMode === 'login' ? 'Contraseña actual' : 'Contraseña'} className="w-full bg-transparent outline-none placeholder:text-slate-400" /></div>{leadErrors.password && <p className="mt-2 text-xs text-rose-600">{leadErrors.password}</p>}</div>
                      {authMode === 'register' && <div><div className="rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 flex items-center gap-3 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)]"><LockKeyhole className="w-4 h-4 text-slate-400" /><input value={account.confirmPassword} onChange={(event) => updateAccountField('confirmPassword', event.target.value)} type="password" placeholder="Confirmar contraseña" className="w-full bg-transparent outline-none placeholder:text-slate-400" /></div>{leadErrors.confirmPassword && <p className="mt-2 text-xs text-rose-600">{leadErrors.confirmPassword}</p>}</div>}
                    </div>
                  </div>
                  )}
                </div>
                <label className="mt-4 flex items-start gap-3 text-sm leading-6 text-slate-600"><input type="checkbox" checked={lead.consentAccepted} onChange={(event) => updateLeadField('consentAccepted', event.target.checked)} className="mt-1 accent-slate-900" />Acepto el tratamiento de datos para {isAuthenticated || authMode === 'login' ? 'continuar con mi acceso, recibir el diagnóstico y contacto de AppEmprendedor.' : 'crear mi cuenta, recibir el diagnóstico y contacto de AppEmprendedor.'}</label>
                {leadErrors.consentAccepted && <p className="mt-2 text-xs text-rose-600">{leadErrors.consentAccepted}</p>}
                <input value={lead.website} onChange={(event) => updateLeadField('website', event.target.value)} tabIndex={-1} autoComplete="off" className="hidden" />
                <div className="mt-5"><div ref={turnstileContainerRef} className="min-h-[65px]" />{leadErrors.turnstileToken && <p className="mt-2 text-xs text-rose-600">{leadErrors.turnstileToken}</p>}<p className="mt-2 text-xs text-slate-500">Protegido por Cloudflare Turnstile</p></div>
                {submitState.message && <div className={`mt-5 rounded-2xl px-4 py-3 text-sm ${submitState.type === 'success' ? 'border border-emerald-200 bg-emerald-50 text-emerald-800' : 'border border-rose-200 bg-rose-50 text-rose-700'}`}>{submitState.message}</div>}
                <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <button type="button" onClick={closeRegistrationModal} className="rounded-2xl border border-slate-300 bg-white/75 px-5 py-3 font-semibold text-slate-700">Seguir editando</button>
                  <button type="submit" disabled={sending} className="rounded-2xl bg-[linear-gradient(135deg,#1f3b35,#2d5a4e)] px-5 py-3 font-semibold text-white shadow-[0_14px_30px_rgba(31,59,53,0.24)] disabled:opacity-60">{sending ? (isAuthenticated || authMode === 'login' ? 'Ingresando...' : 'Creando cuenta...') : (isAuthenticated ? 'Continuar y ver resumen' : authMode === 'login' ? 'Iniciar sesión y ver resumen' : 'Crear cuenta y ver resumen')}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {hasUnlockedDashboard && <section ref={dashboardSectionRef} className="border-y border-slate-200 bg-white/80"><div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16"><div className="rounded-[32px] border border-slate-200 bg-[linear-gradient(180deg,#faf8f4_0%,#ffffff_100%)] p-6 sm:p-8 shadow-[0_24px_60px_rgba(15,23,42,0.06)]"><div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between"><div><p className="text-xs uppercase tracking-[0.2em] text-slate-500">Resumen visual previo</p><h2 className="mt-3 text-3xl sm:text-4xl font-display">Lectura ejecutiva del potencial de automatización</h2><p className="mt-4 max-w-3xl text-sm sm:text-base leading-7 text-slate-600">Este bloque resume el diagnóstico y conecta de forma directa con el informe ejecutivo que aparece a continuación.</p></div><div className="rounded-2xl border border-slate-200 bg-white px-5 py-4 xl:max-w-sm"><p className="text-xs uppercase tracking-[0.16em] text-slate-500">Proceso analizado</p><p className="mt-2 text-lg font-semibold">{wizard.businessIdea}</p></div></div><div className="mt-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">{lockedCards.map(([label, value, tone]) => (<article key={label} className="flex min-h-[164px] flex-col justify-between rounded-[24px] border border-slate-200 bg-white px-5 py-5 shadow-[0_16px_32px_rgba(15,23,42,0.04)]"><p className="text-xs uppercase tracking-[0.16em] text-slate-500">{label}</p><p className={`mt-4 text-2xl font-semibold ${tone}`}>{value}</p></article>))}</div></div><div className="mt-10"><div><p className="text-xs uppercase tracking-[0.2em] text-slate-500">Dashboard gerencial final</p><p className="mt-3 max-w-3xl text-sm sm:text-base leading-7 text-slate-600">Con base en la información suministrada, este diagnóstico presenta una estimación orientativa del ahorro, el payback y el posible retorno de automatizar el proceso analizado.</p></div><div className="mt-8 grid md:grid-cols-2 xl:grid-cols-6 gap-4">{[['Ahorro mensual estimado', formatMoney(metrics.monthlySavings, wizard.currency), 'text-emerald-700'], ['Ahorro anual estimado', formatMoney(metrics.annualSavings, wizard.currency), 'text-emerald-700'], ['Costo operativo actual', formatMoney(metrics.monthlyManualCost, wizard.currency), 'text-rose-700'], ['Eficiencia potencial', `${formatNumber(metrics.efficiencyPotential, 0)}%`, 'text-sky-700'], ['Payback mensual', metrics.paybackMonths ? `${formatNumber(metrics.paybackMonths, 1)} meses` : 'Por validar', 'text-amber-700'], ['Payback anual', metrics.paybackYears ? `${formatNumber(metrics.paybackYears, 1)} años` : 'Por validar', 'text-amber-700']].map(([label, value, tone]) => (<article key={label} className="rounded-[26px] border border-slate-200 bg-white px-5 py-5 shadow-[0_16px_32px_rgba(15,23,42,0.05)]"><p className="text-xs uppercase tracking-[0.16em] text-slate-500">{label}</p><p className={`mt-3 text-2xl font-semibold ${tone}`}>{value}</p></article>))}</div><div className="mt-10 grid lg:grid-cols-[1.1fr_0.9fr] gap-6"><div className="rounded-[28px] border border-slate-200 bg-white px-6 py-6 shadow-[0_16px_32px_rgba(15,23,42,0.05)]"><div className="flex items-center justify-between"><h3 className="text-xl font-display">Comparación actual vs. potencial</h3><LayoutDashboard className="w-5 h-5 text-slate-700" /></div><div className="mt-6 space-y-5">{currentVsPotential.map(([label, value, barClass]) => (<div key={label}><div className="flex items-center justify-between gap-4"><p className="text-sm font-medium text-slate-700">{label}</p><span className="text-sm text-slate-500">{formatMoney(value, wizard.currency)}</span></div><div className="mt-2 h-3 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full rounded-full ${barClass}`} style={{ width: `${(Number(value) / maxComparisonValue) * 100}%` }} /></div></div>))}</div></div><div className="space-y-6"><div className="rounded-[28px] border border-slate-200 bg-[#1f3b35] px-6 py-6 text-slate-50 shadow-[0_16px_32px_rgba(15,23,42,0.08)]"><h3 className="text-xl font-display">Impacto operativo</h3><div className="mt-5 grid grid-cols-2 gap-4"><div className="rounded-2xl bg-white/8 px-4 py-4"><p className="text-xs uppercase tracking-[0.16em] text-slate-300">Horas recuperables al mes</p><p className="mt-2 text-2xl font-semibold">{formatNumber(metrics.recoveredHoursMonthly, 0)} h</p></div><div className="rounded-2xl bg-white/8 px-4 py-4"><p className="text-xs uppercase tracking-[0.16em] text-slate-300">Inversión evaluada</p><p className="mt-2 text-2xl font-semibold">{formatMoney(metrics.implementationCost, wizard.currency)}</p><p className="mt-2 text-xs text-slate-300">Referencia sugerida: {formatMoney(metrics.suggestedInvestment, wizard.currency)}</p></div></div></div><div className="rounded-[28px] border border-indigo-200 bg-[radial-gradient(circle_at_top,#eef2ff_0%,#ffffff_62%)] px-6 py-6 shadow-[0_18px_38px_rgba(79,70,229,0.14)]" style={{ animation: 'roiPulseGlow 2.8s ease-in-out infinite' }}><h3 className="text-xl font-display text-slate-900">Recomendación final</h3><p className="mt-4 text-sm leading-7 text-slate-600">{buildRecommendation(metrics)}</p><div className="mt-5 rounded-[24px] border border-indigo-200 bg-[linear-gradient(135deg,#312e81,#4338ca,#6366f1)] px-5 py-5 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.18)]"><p className="text-xs uppercase tracking-[0.18em] text-indigo-100">ROI estimado a {formatRoiHorizon(metrics.roiYears)}</p><p className="mt-3 text-4xl sm:text-5xl font-semibold leading-none text-white drop-shadow-[0_10px_24px_rgba(15,23,42,0.22)]">{metrics.roiProjected !== null ? `${formatNumber(metrics.roiProjected, 0)}%` : 'Por validar'}</p><p className="mt-3 text-sm text-indigo-100">Lectura ejecutiva del retorno potencial del proyecto.</p></div><div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-4"><p className="text-xs uppercase tracking-[0.16em] text-amber-700">Importante</p><p className="mt-2 text-sm leading-7 text-amber-900">Este cálculo es una estimación orientativa basada en los datos proporcionados. El resultado real puede variar según la implementación y los procesos específicos de la empresa.</p></div></div></div></div></div></div></section>}

        <section id="roi-intro" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid lg:grid-cols-[0.95fr_1.05fr] gap-8">
            <article className="rounded-[28px] border border-slate-200 bg-white px-6 py-6"><h2 className="text-2xl font-display">Cómo usar esta calculadora ROI automatización</h2><p className="mt-4 text-sm leading-7 text-slate-600">Esta calculadora ROI automatización estima el costo actual de un proceso manual y el ahorro por automatizar procesos para empresas pequeñas. Parte del tiempo perdido, las personas involucradas, la inversión inicial y el costo laboral promedio para traducir la operación en dinero.</p><p className="mt-4 text-sm leading-7 text-slate-600">Si estás evaluando automatización de procesos para empresas pequeñas, este diagnóstico te da una lectura accionable sin pedir información técnica. El dashboard completo aparece cuando ya existe suficiente contexto para entregar un resultado ejecutivo serio.</p></article>
            <article className="rounded-[28px] border border-slate-200 bg-[#faf8f4] px-6 py-6"><h2 className="text-2xl font-display">Qué calcula exactamente</h2><div className="mt-5 space-y-3 text-sm leading-7 text-slate-600"><p><span className="font-semibold text-slate-900">Costo hora:</span> salario mensual / 160.</p><p><span className="font-semibold text-slate-900">Costo manual mensual:</span> horas perdidas por semana x 4 x costo hora x personas involucradas.</p><p><span className="font-semibold text-slate-900">Ahorro mensual:</span> costo manual mensual x porcentaje estimado de automatización.</p><p><span className="font-semibold text-slate-900">Ahorro anual:</span> ahorro mensual x 12.</p><p><span className="font-semibold text-slate-900">Payback mensual:</span> inversión / ahorro mensual.</p><p><span className="font-semibold text-slate-900">Payback anual:</span> payback mensual / 12.</p><p><span className="font-semibold text-slate-900">ROI al horizonte elegido:</span> ((ahorro anual x años elegidos) - inversión) / inversión.</p></div></article>
          </div>
        </section>
      </main>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <p className="text-sm text-slate-500">AppEmprendedor · ahorro por automatizar procesos con una lectura ejecutiva clara y accionable.</p>
          <div className="flex gap-4 text-sm">
            <Link to="/" className="text-slate-600 hover:text-slate-900">Inicio</Link>
            <a href="#roi-wizard" className="text-slate-600 hover:text-slate-900">Diagnóstico</a>
            <a href="/politica-de-privacidad" className="text-slate-600 hover:text-slate-900">Política de privacidad</a>
          </div>
        </div>
      </footer>
    </div>
  )
}
